import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { JobStatus, ReportType } from '@/api/generatedApi';
import { REPORT_POLL_INTERVAL_MS } from '@/hooks/useDataPolling/pollingConstants';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';

export const LATEST_DUPLICATION_REPORT_QUERY_KEY = 'reports.latestDuplicationReport';

export function useGetLatestDuplicationReport({ pollWhileRunning }: { pollWhileRunning?: boolean } = {}) {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const query = useQuery({
    queryKey: [LATEST_DUPLICATION_REPORT_QUERY_KEY, { tenantId }],
    queryFn: async () =>
      api.reports.listReports({
        tenantId,
        page: 1,
        perPage: 1,
        type: ReportType.DUPLICATION,
        status: JobStatus.COMPLETED,
      }),
    select: data => data.data.reports.at(0),
    enabled: !!tenantId,
    placeholderData: previousData => previousData,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    // A duplication report that is still running is not in this result set, so the summary
    // only refreshes on its own while the tile says one is on the way.
    refetchInterval: pollWhileRunning ? REPORT_POLL_INTERVAL_MS : false,
  });

  const { refetch } = query;
  const wasRunningReference = useRef(!!pollWhileRunning);

  // Polling stops the moment the list reports the run finished, and that can fall between two
  // polls, leaving the summary naming the report before it. Switching polling off is not enough:
  // fetch once more on the way down.
  useEffect(() => {
    if (wasRunningReference.current && !pollWhileRunning) void refetch();
    wasRunningReference.current = !!pollWhileRunning;
  }, [pollWhileRunning, refetch]);

  return query;
}
